import { useCallback } from 'react';

import { useSettings } from '@/data/Settings';
import { useTrackException } from 'presentation/events/hooks/useTrackException';
import { setCookieCAPI } from 'presentation/events/utils/eventCookieUtils';

import { TrackPageViewProps } from './types';
import { useTrackPageViewFacebook } from './useTrackPageViewFacebook';
import { useTrackPageViewGA4 } from './useTrackPageViewGA4';
import { useTrackPageViewTikTok } from './useTrackPageViewTikTok';
import { useTrackViewCategoryPinterest } from './useTrackViewCategoryPinterest';
import { useTrackPageViewPinterest } from './useTrackPageViewPinterest';

export const useTrackPageView = () => {
	const { settings } = useSettings();
	const { storeId } = settings;
	const trackException = useTrackException();

	const sendGA4Event = useTrackPageViewGA4();
	const { sendFacebookEvent } = useTrackPageViewFacebook();
	const { sendTiktokEvent } = useTrackPageViewTikTok();
	const { sendPinterestEvent, shouldSendViewCategory } = useTrackViewCategoryPinterest();
	const { sendPinterestPageViewEvent } = useTrackPageViewPinterest();

	return useCallback(
		(props: TrackPageViewProps) => {
			try {
				// The Pinterest pixel is triggered by the GA4 page_view and reads the mfbt cookie at that moment,
				// so mfbt must be set before sendGA4Event. We keep the same id to send our view_category (CAPI)
				// so it dedupes with the pixel. Only overwrite mfbt when view_category is actually sent; otherwise
				// we clobber the id set by other events (e.g. search_submit) and break their dedupe.
				// view_category must be sent after the page_view.
				const viewCategoryEventId = shouldSendViewCategory ? setCookieCAPI('mfbt') : undefined;
				const eventId = setCookieCAPI('_mfbt');

				sendGA4Event({ userId: props.userId, isLoggedIn: props.isLoggedIn });

				sendFacebookEvent({ eventId, personInfo: props.personInfo, storeId });

				sendTiktokEvent({ eventId, personInfo: props.personInfo, storeId });

				sendPinterestPageViewEvent({ eventId, personInfo: props.personInfo, storeId });

				if (viewCategoryEventId) {
					sendPinterestEvent({
						eventId: viewCategoryEventId,
						personInfo: props.personInfo,
						storeId,
					});
				}
			} catch (e) {
				console.error('Error sending page view event', e);
				trackException(e);
			}
		},
		[
			sendFacebookEvent,
			sendGA4Event,
			sendPinterestEvent,
			sendPinterestPageViewEvent,
			sendTiktokEvent,
			shouldSendViewCategory,
			storeId,
			trackException,
		]
	);
};
